import { ref } from "vue";
import { useApi, useToaster, type CreateOrUpdateDraftLeadRequest, type Urgency } from "@shopexperts/shared";
import { useQueryClient } from "@tanstack/vue-query";

export interface CreateDraftLeadPayload {
    email: string;
}

export interface UpdateDraftLeadPayload {
    token: string;
    firstName?: string;
    lastName?: string;
    projectTitle?: string;
    projectDescription?: string;
    storeUrl?: string;
    brandName?: string;
    selectedExpertId?: number;
    selectedPackagedServiceId?: number;
    urgency?: Urgency;
    matchingStrategy?: "selected" | "selected-auto" | "auto";
    projectValueRangeId?: number;
    timezone?: string;
}

export interface CreateOrUpdateDraftLeadResult {
    success: boolean;
    token: string;
    isComplete: boolean;
    missingFields: string[];
}

/**
 * Composable for creating or updating a draft lead.
 * 
 * This supports the progressive lead submission flow:
 * 1. Create a draft with just an email -> receive a token
 * 2. Update the draft with more fields using the token
 * 
 * @param options - Configuration options
 * @param options.showToast - Whether to show success/error toasts (default: false for updates)
 * 
 * @example
 * ```ts
 * const { createDraft, updateDraft, isSubmitting } = useCreateOrUpdateDraftLead();
 * 
 * // Step 1: Create draft with email
 * const { token } = await createDraft({ email: 'user@example.com' });
 * 
 * // Step 2: Update draft with more info
 * await updateDraft({
 *   token,
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   projectTitle: 'My Project',
 * });
 * ```
 */
export function useCreateOrUpdateDraftLead(options: { showToast?: boolean } = {}) {
    const { showToast = false } = options;
    const api = useApi();
    const toaster = useToaster();
    const queryClient = useQueryClient();

    const isSubmitting = ref(false);

    /**
     * Create a new draft lead with an email address.
     * Returns a token to use for subsequent updates.
     */
    async function createDraft(payload: CreateDraftLeadPayload): Promise<CreateOrUpdateDraftLeadResult> {
        isSubmitting.value = true;

        try {
            const result = await api.public.draftLeads.createOrUpdate({
                body: {
                    email: payload.email,
                },
            });

            return {
                success: true,
                token: result.token,
                isComplete: result.is_complete,
                missingFields: result.missing_fields,
            };
        } catch (error: any) {
            console.error("[useCreateOrUpdateDraftLead] Create error:", error);

            if (showToast) {
                toaster.create({
                    title: "Failed to start draft",
                    description: error?.response?.data?.message || "Unable to create your draft. Please try again.",
                    type: "error",
                });
            }

            throw error;
        } finally {
            isSubmitting.value = false;
        }
    }

    /**
     * Update an existing draft lead using its token.
     */
    async function updateDraft(payload: UpdateDraftLeadPayload): Promise<CreateOrUpdateDraftLeadResult> {
        isSubmitting.value = true;

        try {
            const body: CreateOrUpdateDraftLeadRequest = {
                token: payload.token,
            };

            // Map camelCase to snake_case
            if (payload.firstName !== undefined) body.first_name = payload.firstName;
            if (payload.lastName !== undefined) body.last_name = payload.lastName;
            if (payload.projectTitle !== undefined) body.project_title = payload.projectTitle;
            if (payload.projectDescription !== undefined) body.project_description = payload.projectDescription;
            if (payload.storeUrl !== undefined) body.store_url = payload.storeUrl;
            if (payload.brandName !== undefined) body.brand_name = payload.brandName;
            if (payload.selectedExpertId !== undefined) body.selected_expert_id = payload.selectedExpertId;
            if (payload.selectedPackagedServiceId !== undefined) body.selected_packaged_service_id = payload.selectedPackagedServiceId;
            if (payload.urgency !== undefined) body.urgency = payload.urgency;
            if (payload.matchingStrategy !== undefined) body.matching_strategy = payload.matchingStrategy;
            if (payload.projectValueRangeId !== undefined) body.project_value_range_id = payload.projectValueRangeId;
            if (payload.timezone !== undefined) body.timezone = payload.timezone;

            const result = await api.public.draftLeads.createOrUpdate({ body });

            // Refresh the draft payload without also refetching sibling queries such as check-auth.
            queryClient.invalidateQueries({ queryKey: ["public", "draft-leads", payload.token], exact: true });

            return {
                success: true,
                token: result.token,
                isComplete: result.is_complete,
                missingFields: result.missing_fields,
            };
        } catch (error: any) {
            console.error("[useCreateOrUpdateDraftLead] Update error:", error);

            if (showToast) {
                toaster.create({
                    title: "Failed to save draft",
                    description: error?.response?.data?.message || "Unable to save your progress. Please try again.",
                    type: "error",
                });
            }

            throw error;
        } finally {
            isSubmitting.value = false;
        }
    }

    return {
        createDraft,
        updateDraft,
        isSubmitting,
    };
}
